import React, { useState, useEffect, useCallback } from 'react';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  Visibility,
  Edit,
  VisibilityOff,
} from '@mui/icons-material';
import { Exercice, Chapitre, Lecon } from '../../types';
import ChapitresLeconsApiService from '../../services/chapitresLeconsApi';
import ExercicesApiService from '../../services/exercicesApi';
import ExerciceFormReadOnly from './ExerciceFormReadOnly';
import MobilePreview from '../common/MobilePreview';
import ExerciceMobileView from './ExerciceMobileView';
import { logger } from '../../lib/logger';
import { adresseExplorateur } from '../../features/contenu/adresses';

/**
 * Page de visualisation d'un exercice
 * Layout split-screen: détails à gauche, preview mobile à droite
 * Même layout que l'éditeur avancé mais en mode lecture seule
 */
const ExerciceView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [exercice, setExercice] = useState<Exercice | null>(null);
  const [chapitreTitre, setChapitreTitre] = useState<string>('');
  const [leconTitre, setLeconTitre] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(true);
  const { environment: env } = useEnvironment();

  const loadData = useCallback(async () => {
    if (!id) {
      setError('ID d\'exercice manquant');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Charger les données de base
      const [exerciceData, chapitres, lecons] = await Promise.all([
        ExercicesApiService.getExerciceById(env, id),
        ChapitresLeconsApiService.getChapitres(env),
        ChapitresLeconsApiService.getLecons(env),
      ]);

      setExercice(exerciceData);

      // Trouver le titre du chapitre
      const chapitre = chapitres.find((ch: Chapitre) => ch.id === exerciceData.chapitreId);
      if (chapitre) {
        setChapitreTitre(chapitre.titre);
      }

      // Trouver le titre de la leçon
      const lecon = lecons.find((lec: Lecon) => lec.id === exerciceData.leconId);
      if (lecon) {
        setLeconTitre(lecon.titre);
      }
    } catch (err) {
      logger.error('Erreur lors du chargement de l\'exercice:', err);
      setError('Impossible de charger l\'exercice');
    } finally {
      setLoading(false);
    }
  }, [id, env]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleBack = () => {
    navigate(adresseExplorateur({ chapitre: exercice?.chapitreId, onglet: 'exercices', ouvert: exercice?.id }));
  };

  const handleEdit = () => {
    navigate(`/exercices/${id}/edit-advanced`);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ bgcolor: '#f5f5f5' }}
      >
        <CircularProgress size={48} />
        <Typography variant="h6" sx={{ mt: 2 }} color="textSecondary">
          Chargement de l'exercice...
        </Typography>
      </Box>
    );
  }

  if (error || !exercice) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Exercice introuvable'}</Alert>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mt: 2 }}>
          Retour à la liste
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', py: 3 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <IconButton onClick={handleBack} size="small">
              <ArrowBack />
            </IconButton>
            <Typography variant="h4" sx={{ flexGrow: 1 }}>
              {exercice.titre}
            </Typography>
            <Tooltip title={showPreview ? 'Masquer la prévisualisation' : 'Afficher la prévisualisation'}>
              <IconButton onClick={() => setShowPreview(!showPreview)} color="primary">
                {showPreview ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<Edit />}
              onClick={handleEdit}
            >
              Modifier
            </Button>
          </Box>
          <Typography variant="body2" color="textSecondary">
            {'Visualisation de l\'exercice avec aperçu mobile'}
          </Typography>
        </Box>

        {/* Split Layout */}
        <Grid container spacing={3}>
          {/* Détails de l'exercice - Gauche */}
          <Grid item xs={12} lg={showPreview ? 7 : 12}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <ExerciceFormReadOnly exercice={exercice} />
            </Paper>
          </Grid>

          {/* Preview Mobile - Droite */}
          {showPreview && (
            <Grid item xs={12} lg={5}>
              <Box
                sx={{
                  position: 'sticky',
                  top: 16,
                  maxHeight: 'calc(100vh - 32px)',
                  overflowY: 'auto',
                }}
              >
                <MobilePreview title="Aperçu Mobile">
                  <ExerciceMobileView
                    exercice={exercice}
                    chapitreTitre={chapitreTitre}
                    leconTitre={leconTitre}
                  />
                </MobilePreview>
              </Box>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default ExerciceView;
